/**
 * Why the assistant did not answer, as a sentence the reader's language has
 * (never the API's English `message`). K1 contract, "SSE events": the five
 * knowledge_* codes arrive as an `error` frame; before the stream opens the
 * request can be refused with the K0 403 `{ error: 'module_disabled' }`, the
 * AI-allowance quota guard's 429 codes, the per-person throttle's 429
 * `knowledge_rate_limited`, or a 400 carrying a knowledge code.
 */

export const ASK_FAILURES = [
  'disabled',
  'limit',
  'tooLong',
  'unavailable',
  'conversationNotFound',
  'moduleDisabled',
  'allowance',
  'rateLimited',
  'signedOut',
  'generic',
] as const
export type AskFailure = (typeof ASK_FAILURES)[number]

const CODE_FAILURE: Record<string, AskFailure> = {
  knowledge_assistant_disabled: 'disabled',
  knowledge_daily_limit_reached: 'limit',
  knowledge_question_too_long: 'tooLong',
  knowledge_ai_unavailable: 'unavailable',
  knowledge_conversation_not_found: 'conversationNotFound',
  knowledge_module_disabled: 'moduleDisabled',
  knowledge_rate_limited: 'rateLimited',
  // The AI-allowance quota guard (apps/api/src/modules/ai-metering/quota-exceeded.exception.ts).
  quota_exhausted_use_own_key: 'allowance',
  daily_ai_limit_reached: 'allowance',
  quota_exceeded: 'allowance',
}

/** The failure an error code names; anything unknown is the generic sentence. */
export function failureFromCode(code: string | null | undefined): AskFailure {
  return (code && CODE_FAILURE[code]) || 'generic'
}

/** The failure an HTTP refusal (before the stream opens) names. */
export function failureFromResponse(status: number, body: unknown): AskFailure {
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  if (record.error === 'module_disabled') return 'moduleDisabled'
  const code = typeof record.errorCode === 'string' ? record.errorCode : null
  if (code && CODE_FAILURE[code]) return CODE_FAILURE[code]
  if (status === 401) return 'signedOut'
  if (status === 429) return 'allowance'
  return 'generic'
}

/** The refusals asking again cannot fix: the question box locks. */
const TERMINAL: ReadonlySet<AskFailure> = new Set(['disabled', 'limit', 'moduleDisabled', 'allowance', 'signedOut'])
export function isTerminalFailure(failure: AskFailure | null | undefined): boolean {
  return !!failure && TERMINAL.has(failure)
}

/** The catalogue keys for each failure's title and sentence (messages/knowledge-portal). */
export const ASK_FAILURE_KEYS: Record<AskFailure, { title: string; body: string }> = {
  disabled: { title: 'knowledgePortal.errors.disabled.title', body: 'knowledgePortal.errors.disabled.body' },
  limit: { title: 'knowledgePortal.errors.limit.title', body: 'knowledgePortal.errors.limit.body' },
  tooLong: { title: 'knowledgePortal.errors.tooLong.title', body: 'knowledgePortal.errors.tooLong.body' },
  unavailable: { title: 'knowledgePortal.errors.unavailable.title', body: 'knowledgePortal.errors.unavailable.body' },
  conversationNotFound: {
    title: 'knowledgePortal.errors.conversationNotFound.title',
    body: 'knowledgePortal.errors.conversationNotFound.body',
  },
  moduleDisabled: { title: 'knowledgePortal.errors.moduleDisabled.title', body: 'knowledgePortal.errors.moduleDisabled.body' },
  allowance: { title: 'knowledgePortal.errors.allowance.title', body: 'knowledgePortal.errors.allowance.body' },
  rateLimited: { title: 'knowledgePortal.errors.rateLimited.title', body: 'knowledgePortal.errors.rateLimited.body' },
  signedOut: { title: 'knowledgePortal.errors.signedOut.title', body: 'knowledgePortal.errors.signedOut.body' },
  generic: { title: 'knowledgePortal.errors.generic.title', body: 'knowledgePortal.errors.generic.body' },
}
